import * as React from 'react';
import { SpaceProps, space as spaceFn, ResponsiveValue, style } from 'styled-system';
import styled from 'styled-components';
import { Translate } from '@packages/i18n';

import { Button, Flex, themeGet, applyShowElement, ShowElementProps, isNot, is, space } from '@packages/ui';

export const LoadMoreInfo = styled.div`
  font-size: ${themeGet.fontSize(6)};
  color: ${themeGet.colors('kilamanjaro')};
`;

export const LoadMoreButtonWrapper = styled(Flex)`
  width: 100%;
  justify-content: center;
  align-items: center;
  z-index: ${themeGet.zIndex('UNDERLAY', 1)};
  position: absolute;
  top: ${themeGet.space(5)};
`;

export const LoadMoreWrapper = styled.div<SpaceProps>`
  text-align: center;
  position: relative;
  ${spaceFn}
`;

const FakeTile = styled.div<ShowElementProps & { tileMargin: ResponsiveValue<string>; isFirst: boolean }>`
  flex: 1 1 0;
  height: 123px;
  background-color: rgb(244, 243, 242);
  ${applyShowElement}

  ${isNot('isFirst')`
    ${style({
      prop: 'tileMargin',
      cssProperty: 'margin-left',
    })}
  `}

  ${is('isFirst')`
    margin-left: 0;
  `}
`;

const RelativePositionWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const FakeGridWrapper = styled.div`
  position: relative;
  width: 100%;
  &::after {
    content: ' ';
    position: absolute;
    width: calc(100% + 8px);
    height: 100%;
    top: 0;
    left: -4px;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0.7) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  display: flex;
  flex-direction: row;
  padding-bottom: ${themeGet.space(1)};
`;

const LoadMoreButton = styled(Button)<SpaceProps>`
  ${spaceFn};
`;

interface LoadMoreGridSectionProps {
  gridColsCount: number[];
  itemsCount: number;
  totalCount: string;
  onClick: VoidFunction;
  disabled?: boolean;
  renderAfter?: React.ReactNode;
}

type TileShowConfig = { [key: number]: ResponsiveValue<boolean> };
const calculateTileShowConfig = (gridColsCount: number[], maxTiles: number) => {
  const maxTilesArr = [...Array(maxTiles)].map((_, index) => index);
  return gridColsCount.reduce<TileShowConfig>((acc: any, cols) => {
    maxTilesArr.forEach((tileIdx) => {
      if (!acc[tileIdx]) {
        acc[tileIdx] = [];
      }

      acc[tileIdx].push(tileIdx < cols);
    });
    return acc;
  }, {});
};
const HORIZONTAL_PADDING = ['2px', '2px', '6px'];

export const LoadMoreGridSection: React.FC<LoadMoreGridSectionProps> = ({
  itemsCount,
  gridColsCount,
  disabled,
  onClick,
  totalCount,
  children,
  renderAfter,
}) => {
  const initialMaxTiles = Math.max(...gridColsCount);
  const [isDisabled, setIsDisabled] = React.useState<boolean>(disabled || false);
  const [maxTiles, setMaxTiles] = React.useState(initialMaxTiles);
  const [tileShowConfig, setTileShowConfig] = React.useState<TileShowConfig>(
    calculateTileShowConfig(gridColsCount, initialMaxTiles),
  );

  React.useEffect(() => {
    const maxTiles = Math.max(...gridColsCount);
    setMaxTiles(maxTiles);

    // setup for which fake-tile to show on which breakpoint
    setTileShowConfig(calculateTileShowConfig(gridColsCount, maxTiles));
  }, [gridColsCount]);

  const handleOnClick = () => {
    if (!disabled) {
      setIsDisabled(true);
      setTimeout(() => onClick && onClick(), 0);
    }
  };

  React.useEffect(() => {
    setIsDisabled(false);
  }, [itemsCount]);

  return (
    <>
      <LoadMoreWrapper px={HORIZONTAL_PADDING} mt={['4px', '4px', '4px', '8px']} mb={`${space[2] + space[6]}px`}>
        {children}
        <RelativePositionWrapper>
          <LoadMoreButtonWrapper flexDirection="column" justifyContent="center">
            <LoadMoreInfo>
              <Translate
                translation={{
                  key: 'productsCountOutofTotal',
                  values: { totalCount, productCount: itemsCount },
                }}
              />
            </LoadMoreInfo>
            <LoadMoreButton disabled={isDisabled} onClick={handleOnClick} mt={3}>
              <Translate translation="loadMore" />
            </LoadMoreButton>
          </LoadMoreButtonWrapper>
          <FakeGridWrapper>
            {[...Array(maxTiles)].map((_, idx) => (
              <FakeTile
                key={idx}
                show={tileShowConfig[idx]}
                isFirst={idx === 0}
                tileMargin={['6px', '6px', '6px', '12px']}
              />
            ))}
          </FakeGridWrapper>
        </RelativePositionWrapper>
      </LoadMoreWrapper>
      {/* This area is reserved for putting extra CMS content that fits in grid */}
      {renderAfter ? <LoadMoreWrapper px={HORIZONTAL_PADDING}>{renderAfter}</LoadMoreWrapper> : null}
    </>
  );
};
